import { quantile } from "simple-statistics";
import { z } from "zod";
import {
  isCompletedCallMetadata
} from "../entities/interview.js";
import {
  checkIsValidInterviewEmail,
  isNotNullNotUndefined,
  isNotUndefined,
  splitSummaryTextIntoLines
} from "./utilities.js";
const getInterviewInsightsSummaryText = (interview) => {
  const { strengths, weaknesses, summary } = interview.insights || {};
  if (summary) return splitSummaryTextIntoLines(summary).join("\n");
  if (!strengths || !weaknesses || strengths.length === 0 && weaknesses.length === 0)
    return "";
  return `Strengths:
${strengths.join("\n")}

Weaknesses:
${weaknesses.join(
    "\n"
  )}`;
};
const InterviewPageSpecialMode = {
  InitialLandingPage: "ilp"
  // Used by Fountain integration to redirect applicants to the interview page
};
const allInterviewPageSpecialModes = [
  InterviewPageSpecialMode.InitialLandingPage
];
const interviewPageSpecialModeSchema = z.enum(
  allInterviewPageSpecialModes
);
const INTERVIEW_LINK_PARAMS = {
  EMAIL: "e",
  MODE: "m"
};
const parseInterviewLinkParams = (urlSearchString) => {
  try {
    const params = new URLSearchParams(urlSearchString);
    const email = params.get(INTERVIEW_LINK_PARAMS.EMAIL);
    const mode = params.get(INTERVIEW_LINK_PARAMS.MODE);
    return {
      email: email && checkIsValidInterviewEmail(email) ? email : void 0,
      mode: interviewPageSpecialModeSchema.safeParse(mode).data
    };
  } catch {
    return {};
  }
};
function findPrimaryCall(interviewMetadata) {
  const calls = interviewMetadata.calls ?? [];
  const completedCalls = calls.filter(isCompletedCallMetadata);
  const [firstCompletedCall] = completedCalls;
  if (firstCompletedCall === void 0) {
    return null;
  }
  return completedCalls.reduce((longest, current) => {
    const longestDuration = new Date(longest.end_time).getTime() - new Date(longest.start_time).getTime();
    const currentDuration = new Date(current.end_time).getTime() - new Date(current.start_time).getTime();
    if (currentDuration > longestDuration) {
      return current;
    }
    if (currentDuration === longestDuration) {
      return new Date(current.start_time) > new Date(longest.start_time) ? current : longest;
    }
    return longest;
  }, firstCompletedCall);
}
const cefrPrimaryCallDurationThresholdMs = 2 * 60 * 1e3;
function getCefrCustomFieldDisplayValue(interview) {
  if (!interview.metadata) {
    return void 0;
  }
  const primaryCall = findPrimaryCall(interview.metadata);
  if (!primaryCall) {
    return void 0;
  }
  const callDurationMs = new Date(primaryCall.end_time).getTime() - new Date(primaryCall.start_time).getTime();
  if (callDurationMs < cefrPrimaryCallDurationThresholdMs) {
    return "The interview was too short to correctly assess the candidate's language level";
  }
  return void 0;
}
function computeAssessmentScorePercentiles(interviews) {
  const mostRecentAssessmentScoreByContactId = new Map(
    interviews.map((interview) => {
      const contactId = interview.contact_id;
      const score = interview.insights?.assessment_score_measurement?.value ?? interview.insights?.assessment_score;
      return contactId && isNotNullNotUndefined(score) ? [contactId, score] : void 0;
    }).filter(isNotUndefined)
  );
  if (mostRecentAssessmentScoreByContactId.size === 0) {
    return void 0;
  }
  const [p20, p40, p60, p80] = quantile(
    [...mostRecentAssessmentScoreByContactId.values()],
    [0.2, 0.4, 0.6, 0.8]
  );
  if (p20 === void 0 || p40 === void 0 || p60 === void 0 || p80 === void 0) {
    return void 0;
  }
  return { p20, p40, p60, p80 };
}
function renderAssessmentScoreRankLabel(percentiles, score) {
  if (score >= percentiles.p80) {
    return "ahead of the pack";
  } else if (score >= percentiles.p60) {
    return "front of the pack";
  } else if (score >= percentiles.p40) {
    return "middle of the pack";
  } else if (score >= percentiles.p20) {
    return "in the pack";
  } else {
    return "behind the pack";
  }
}
export {
  INTERVIEW_LINK_PARAMS,
  InterviewPageSpecialMode,
  allInterviewPageSpecialModes,
  computeAssessmentScorePercentiles,
  findPrimaryCall,
  getCefrCustomFieldDisplayValue,
  getInterviewInsightsSummaryText,
  interviewPageSpecialModeSchema,
  parseInterviewLinkParams,
  renderAssessmentScoreRankLabel
};
//# sourceMappingURL=interviewHelpers.js.map
